import { Button, Grid, Stack, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import DateRangePicker from '../../components/common/DateRangePicker'
import TextSearchField from '../../components/common/TextSearchField'
import ProductTable from '../../components/product/ProductTable'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { RouteConstants } from '../../navigation/navigation-types'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { ProductManager } from '../../services/api/ProductManager'
import { ProductType, ProductWithStatisticsDto } from '../../services/api/sharedTypes/Product'
import CustomContainer from '../../components/common/CustomContainer'
import { convertToSearchString } from '../../utils/global-utils'
import LinkWrapper from '../../components/common/LinkWrapper'
import CancelFiltersButton from '../../components/common/CancelFiltersButton'

const Product: React.FC = () => {
    const dispatch = useAppDispatch()

    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [searchedValue, setSearchedValue] = useState<string>('')
    const [products, setProducts] = useState<ProductWithStatisticsDto[]>([])
    const [filteredProducts, setFilteredProducts] = useState<ProductWithStatisticsDto[]>()

    const removeProduct = async (id: string) => {
        if (!products) return

        const productManager = ProductManager.getManager()
        try {
            await productManager.deleteProductAsync(id)
            const newProducts = products.filter((product) => product.id !== id)
            setProducts(newProducts)
            setFilteredProducts(newProducts)
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        dispatch(displayMessage({ message: 'Produkt smazán', type: 'success' }))
    }

    //Get products from server
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)

            try {
                const productManager = ProductManager.getManager()
                const productsRes = await productManager.getProductListAsync()
                if (productsRes) {
                    setProducts(productsRes.products)
                    setFilteredProducts(productsRes.products)
                }
            } catch (error) {
                setLoadingData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    useEffect(() => {
        const setFilteredProductsByEventDate = async () => {
            if (!products || products.length <= 0) return
            setLoadingData(true)
            const productManager = ProductManager.getManager()
            if (dateTo && dateFrom) {
                try {
                    if (isNaN(dateTo.getTime()) || isNaN(dateFrom.getTime())) return setLoadingData(false)
                    const productsRes = await productManager?.getAllFilteredProductsAsync(
                        dateFrom.toISOString(),
                        dateTo.toISOString()
                    )
                    if (productsRes) {
                        setFilteredProducts(productsRes.products)
                    }
                } catch (error) {
                    setLoadingData(false)
                    return dispatch(displayMessage({ message: error, type: 'error' }))
                }
            } else {
                const productsRes = await productManager.getProductListAsync()
                if (productsRes) {
                    setProducts(productsRes.products)
                    setFilteredProducts(productsRes.products)
                }
            }
            setLoadingData(false)
        }

        setFilteredProductsByEventDate()
    }, [dateFrom, dateTo, setLoadingData])

    const productsForTable = filteredProducts?.filter(
        (product: ProductWithStatisticsDto) => {
            if (searchedValue) {
                return convertToSearchString(product.name).includes(convertToSearchString(searchedValue))
            }
            return true
        },
        [dateFrom, dateTo, searchedValue]
    )

    const external = productsForTable?.filter((product: ProductWithStatisticsDto) => product.productType === ProductType.External)
    const internal = productsForTable?.filter((product: ProductWithStatisticsDto) => product.productType === ProductType.Internal)

    if (loadingData) return <ScreenLoadingPlaceholder />

    return (
        <CustomContainer>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='h5'>Atrakce a služby</Typography>
                <LinkWrapper route={RouteConstants.addProduct}>
                    <Button variant='contained'>+ Přidat službu</Button>
                </LinkWrapper>
            </Stack>

            <Stack direction='row' spacing={3} my={1} overflow='hidden'>
                <DateRangePicker
                    labelFrom='Události od'
                    labelTo='Události do'
                    dateTo={dateTo}
                    dateFrom={dateFrom}
                    onSetDateFrom={setDateFrom}
                    onSetDateTo={setDateTo}
                />

                <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />

                {
                    searchedValue || dateFrom || dateTo ? (
                        <CancelFiltersButton onClick={
                            () => {
                                setSearchedValue('')
                                setDateFrom(null)
                                setDateTo(null)
                            }
                        }
                        />
                    ) : null
                }
            </Stack>

            <Grid container rowSpacing={4} columnSpacing={4}>
                <Grid item xs={12} xl={6} sx={{ overflow: 'auto' }}>
                    {internal ? (
                        <ProductTable
                            products={internal}
                            onDelete={removeProduct}
                            title='Vlastní'
                        />
                    ) : (
                        <Typography variant='body1'>Nejsou zde žádné vlastní služby</Typography>
                    )}
                </Grid>
                <Grid item xs={12} xl={6} sx={{ overflow: 'auto' }}>
                    {external ? (
                        <ProductTable
                            products={external}
                            onDelete={removeProduct}
                            title='Externí'
                        />
                    ) : (
                        <Typography variant='body1'>Nejsou zde žádné externí služby</Typography>
                    )}
                </Grid>
            </Grid>
        </CustomContainer>
    )
}

export default Product
